import { useRef, useState, useMemo, useEffect } from "react";
import Loading from "../../components/utils/Loading";
import { LoadingScreen } from "../../components/loader/LoadingScreen";
import ActivityCollectionRow from "./ActivityCollectionRow";
import { useTranslation } from "react-i18next";
import useScrollToEnd from "../../hooks/useScrollToEnd";
import ContainerCompact from "../container/ContainerCompact";
import { MaterialCountingPieChart } from "../../components/charts/MaterialCountingPieChart";
import { useActivityCollectionData } from "./hooks/useActivityCollectionData";
import { useWasteCategoryAndClients } from "./hooks/useWasteCategoryAndClients";
import { useCollectionExport } from "./hooks/useCollectionExport";
import { useSuperContainerGrouping } from "./hooks/useSuperContainerGrouping";
import { ActivityShareDownloadIcon } from "./components/ActivityShareExportItems";
import { TableFilterClearLink } from "../../ui/components/filters";

const ActivityCollection = ({
  keywordFilter,
  periodHistoryStart,
  periodHistoryStop,
  superContainers,
  handleValidate,
  onExportStateChange,
  onHistoricalFluxLabels,
  originFilter = "all",
  onHistoricalOriginLabels,
  characterizationFilter = "all",
  characterizationStatusFilter = "all",
  onClearFilters = null,
}) => {
  const { t } = useTranslation();
  const [disabledDownload, setDisabledDownload] = useState(false);
  const [activeWasteCategory, setActiveWasteCategory] = useState("");
  const [chartData, setChartData] = useState({});
  const [displayChartData, setDisplayChartData] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 8;

  const chartRef = useRef();
  const cycleChartRef = useRef();
  const materialChartRef = useRef();

  useScrollToEnd(() => {
    setCurrentPage((prevPage) => prevPage + 1);
  });

  const {
    isLoading,
    dataContainerCollection,
    countDataCollection,
    characterizationCounts,
  } = useActivityCollectionData(
    keywordFilter,
    periodHistoryStart,
    periodHistoryStop,
    handleValidate,
    null,
    onHistoricalFluxLabels,
    originFilter,
    onHistoricalOriginLabels,
    characterizationFilter,
    characterizationStatusFilter,
  );

  const { wasteCategory, corporationClients } = useWasteCategoryAndClients();

  const {
    loadScreen,
    setLoadScreen,
    progress,
    isProgress,
    setIsProgress,
    exportCollectionToCSV,
    handleCsvExport,
    weightToTonnes,
  } = useCollectionExport(
    dataContainerCollection,
    superContainers,
    periodHistoryStart,
    periodHistoryStop,
    activeWasteCategory,
    t,
  );

  const exportCollectionToCSVRef = useRef(exportCollectionToCSV);
  exportCollectionToCSVRef.current = exportCollectionToCSV;

  const { superPairsByTime } = useSuperContainerGrouping(
    dataContainerCollection,
    superContainers,
  );

  useEffect(() => {
    setDisabledDownload(false);
  }, [periodHistoryStart, periodHistoryStop]);

  const exportItems = useMemo(
    () => [
      {
        key: "csv-list",
        label: t("activityExportCsvList"),
        icon: <ActivityShareDownloadIcon />,
        menuIcon: <ActivityShareDownloadIcon />,
        onClick: () => exportCollectionToCSVRef.current(),
      },
    ],
    [t],
  );

  useEffect(() => {
    onExportStateChange?.({
      disabled: disabledDownload || isLoading,
      items: exportItems,
    });
  }, [onExportStateChange, disabledDownload, isLoading, exportItems]);

  return (
    <div className="card-block-activity-item">
      {isLoading ? (
        <Loading color="#F5F5F5" />
      ) : (
        <div>
          {characterizationCounts && characterizationFilter === "sent" && (
            <p className="text-sm text-gray-500 mt-2 mb-0">
              {t("characterizationLevel2Summary", {
                sent: characterizationCounts.sent,
                total: characterizationCounts.total,
              })}
            </p>
          )}
          {characterizationCounts && characterizationFilter === "not_sent" && (
            <p className="text-sm text-gray-500 mt-2 mb-0">
              {t("characterizationLevel1Summary", {
                matched:
                  characterizationCounts.total - characterizationCounts.sent,
                total: characterizationCounts.total,
              })}
            </p>
          )}
          <section className="time-line">
            {countDataCollection === 0 ? (
              characterizationFilter === "sent" ||
              characterizationFilter === "not_sent" ? (
                <div className="no-data">
                  <p className="m-0">
                    {t(
                      characterizationFilter === "sent"
                        ? "No level 2 characterization for this period"
                        : "No level 1 characterization for this period",
                    )}
                  </p>
                  <TableFilterClearLink
                    visible={Boolean(onClearFilters)}
                    onClear={onClearFilters}
                  />
                </div>
              ) : (
                <div className="no-data">{t("noCollection")}</div>
              )
            ) : (
              <div className="time-line-list">
                {dataContainerCollection
                  .slice(0, currentPage * itemsPerPage)
                  .map((timeLine, index) => {
                    const isInSuperContainer = Array.isArray(superContainers)
                      ? superContainers.some((superContainer) =>
                          superContainer?.containerObjectIdList?.includes(
                            timeLine._id,
                          ),
                        )
                      : false;

                    const isLeftImage =
                      timeLine._value_image.includes("-L.jpeg");

                    const shouldRender =
                      (isInSuperContainer && isLeftImage) ||
                      !isInSuperContainer;

                    const superContainerTimeLine =
                      isInSuperContainer && isLeftImage
                        ? superPairsByTime.get(timeLine._time) || null
                        : null;

                    return (
                      shouldRender && (
                        <ActivityCollectionRow
                          key={`${timeLine._measurement}_${timeLine._time}`}
                          timeLine={timeLine}
                          index={index}
                          loadScreen={loadScreen}
                          setLoadScreen={setLoadScreen}
                          handleCsvExport={handleCsvExport}
                          wasteCategory={wasteCategory}
                          superContainers={
                            Array.isArray(superContainers)
                              ? superContainers.find((item) =>
                                  item?.containerObjectIdList?.includes(
                                    timeLine._id,
                                  ),
                                )
                              : undefined
                          }
                          setActiveWasteCategory={setActiveWasteCategory}
                          activeWasteCategory={activeWasteCategory}
                          weightToTonnes={weightToTonnes}
                          setIsProgress={setIsProgress}
                          setChartData={setChartData}
                          chartRef={chartRef}
                          cycleChartRef={cycleChartRef}
                          setDisplayChartData={setDisplayChartData}
                          materialChartRef={materialChartRef}
                          superContainerTimeLine={superContainerTimeLine}
                          corporationClients={corporationClients}
                        />
                      )
                    );
                  })}
              </div>
            )}
          </section>

          {Object.keys(chartData).length > 0 && displayChartData && (
            <div
              aria-hidden
              style={{
                position: "fixed",
                left: "-9999px",
                top: 0,
                width: "1200px",
                opacity: 1,
                pointerEvents: "none",
                zIndex: -9999,
              }}
            >
              <div
                ref={chartRef}
                style={{
                  height: 600,
                  width: 600,
                }}
              >
                <MaterialCountingPieChart
                  dataChart={chartData.dataAnomalyDelivery}
                  labelChart={chartData.labelsAnomalyDelivery}
                  materialTotal={chartData.numberOfAnomaly}
                  chartTotal={chartData.anomalyChartTotal}
                  colorChart={chartData.colorAnomalyDelivery}
                  showMaterialTotal={false}
                  radius={["30%", "60%"]}
                  centerLabelVariant="errors"
                  labelFontSize="0.9rem"
                />
              </div>
              <article
                ref={cycleChartRef}
                className="bg-white rounded-xl"
                style={{ width: 800 }}
              >
                <ContainerCompact
                  container={chartData.container}
                  history={true}
                  dataForCompactOverlay={chartData.historyCycle}
                  findHistoryId={null}
                  showPackmat={false}
                  showLimitFillingLevel={false}
                  showToolbox={false}
                />
              </article>
              <div
                ref={materialChartRef}
                style={{
                  height: 600,
                  width: 600,
                }}
              >
                <MaterialCountingPieChart
                  dataChart={chartData.dataNumberOfObject}
                  labelChart={chartData.labelObjectSeparate}
                  materialTotal={chartData.numberOfObjects}
                  colorChart={chartData.colorObject}
                  showMaterialTotal={false}
                  showToolbox={false}
                  radius={["30%", "60%"]}
                  labelFontSize="0.9rem"
                />
              </div>
            </div>
          )}
          {loadScreen && (
            <LoadingScreen isProgress={isProgress} progress={progress} />
          )}
        </div>
      )}
    </div>
  );
};

export default ActivityCollection;
